import React, { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHydrated } from 'react-hydration-provider'
import { Col, Ratio, Row } from 'react-bootstrap'
import loadable from '@loadable/component'
import type ReactPlayerType from 'react-player/lazy'
import Hls, { ErrorTypes } from 'hls.js'

import { LinkButton, useMatchMedia } from '../../../Common'
import { useNotifications } from '../../../Notifications'

import { Radish } from '../../components/Radish'
import { useLogo } from '../../hooks/useLogo'

import { CONTENT_SOURCES } from '../../AppConfig'

import cheeseOnPaper from '../../../assets/images/cheese-on-paper.jpg?format=webp'
import butter from '../../../assets/images/butter-6000x3669.jpg?format=webp'
import walnutPate from '../../../assets/images/walnut-pate.jpg?format=webp'
import hands from '../../../assets/images/hands.png?format=webp'
import ricota from '../../../assets/images/ricota.jpg?format=webp'
import cow from '../../../assets/images/cow.png?format=webp'

const ReactPlayer = loadable(() => import( 'react-player/lazy' ), { ssr: false } )

export const Course = () => {

    const intl = useIntl()

    const hydrated = useHydrated()

    const { addNotification, removeAllNotifications } = useNotifications()

    const matches = useMatchMedia( '(min-width: 992px)' )

    const courseUrl = intl.locale === 'cs-CZ' ? '/vyber-kurzu' : '/course-selection'

    const logo = useLogo()
    
    const playerRef = useRef<ReactPlayerType | null>( null )
    const [ videoErrorCount, setVideoErrorCount ] = useState( 0 )
    // trick to start playing muted and un-mute when palying started
    const [ muted, setMuted ] = useState( true )

    const errorHandler = () => {
        if( videoErrorCount > 5 ) {
            addNotification({ variant: 'danger', category: 'video', content: intl.formatMessage( { description: 'Play.VideoError', defaultMessage: 'Error fetching video. Try another video or try it later, please.' } ) })
        } else setVideoErrorCount( videoErrorCount + 1 )
    }

    const onHlsError = ( error: unknown, data: { type: ErrorTypes } ) => {
        let recoverable = false
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if( error === 'hlsError' && data && typeof data.type !== 'undefined' ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            switch ( data.type ) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                // try to recover network error
                //console.log('fatal network error encountered, try to recover')
                recoverable = true
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                playerRef.current?.getInternalPlayer( 'hls' ).startLoad()
                break
            case Hls.ErrorTypes.MEDIA_ERROR:
                //console.log('fatal media error encountered, try to recover')
                recoverable = true
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                playerRef.current?.getInternalPlayer( 'hls' ).recoverMediaError()
                break
            }
        }
        if( !recoverable || error !== 'hlsError' ) errorHandler()
    }

    return <div className='text-center justify-middle'>
        <div className='position-relative'>
            <Ratio aspectRatio='16x9' className='image-block mt-0'><>
                { hydrated && <ReactPlayer 
                    ref={ playerRef }
                    url={ CONTENT_SOURCES[ 'aws-cdn' ] + ( intl.locale === 'cs-CZ' ? '/vegilove-trailer-2022-cs' : '/vegilove-trailer-2022-en' ) + '/master.m3u8' }
                    controls
                    muted={ muted }
                    playing
                    pip={ false }
                    width='100%'
                    height='100%'
                    light={ <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
                        <img style={{ width: '100%' }} src={ cheeseOnPaper.src } alt=''/>
                        <h1 className='sweet-belly position-absolute fs-4 top-0 end-0 m-4'><FormattedMessage description='App.Course.TextOverVideo' defaultMessage='Naučte se vyrábět rostlinné sýry doma' /></h1>
                    </div> }
                    onError={ onHlsError }
                    onStart={ () => { 
                        setMuted( false )
                        removeAllNotifications( 'video' )
                    } }
                    onEnded={ () => { 
                        playerRef.current?.showPreview() 
                    } }
                    config={{
                        file: {
                            attributes: {
                                crossOrigin: 'anonymous',
                                playsInline: true,
                                disablePictureInPicture: true
                            },
                            hlsVersion: '1.3.2',
                        }
                    }}
                /> }
            </></Ratio>
        </div>
        <Row className='text-block'>
            <Col>
                <div>
                    <FormattedMessage description='App.Course.T1' defaultMessage='Naučím vás, jak si doma snadno a&nbsp;rychle vyrobit 17 skvělých veganských alternativ běžných mléčných výrobků a&nbsp;ušetřit přitom peníze' />
                </div>
                <div className='image-block'><Radish size={ 100 } rotation={ 90 } /></div>
                <h2 className='text-green'>
                    <FormattedMessage description='App.Course.T2' defaultMessage='V KURZU ZÍSKÁTE RECEPTYNA TYTO ROSTLINNÉ SÝRY, MÁSLO, SMETANY A JOGURTY:' />
                </h2>
            </Col>
        </Row>
        <Ratio aspectRatio='16x9' className='image-block'>
            <img src={ butter.src } alt={ intl.formatMessage({ description: 'App.Course.ButterImage', defaultMessage: 'Butter' }) } />
        </Ratio>
        <Row className='text-block'>
            <Col className='col-12 col-lg-4'>
                <div><FormattedMessage description='App.Course.CreamCheese' defaultMessage='probiotický tvaroh' /></div>
                <div><FormattedMessage description='App.Course.Parmegiano' defaultMessage='parmezán' /></div>
                <div><FormattedMessage description='App.Course.HotCheese' defaultMessage='tekutý sýr na zapékané pokrmy' /></div>
                <div><FormattedMessage description='App.Course.SourCream' defaultMessage='zakysaná smetana' /></div>
                <div><FormattedMessage description='App.Course.FermetedCheese' defaultMessage='fermentovaný sýr' /></div>
            </Col>
            <div className='free-image-block' style={ matches ? { bottom: 0, left: '30%' } : { top: '20%', left: '10%' } }><Radish size={ 50 } rotation={ 180 } /></div>
            <Col className='col-12 col-lg-4'>
                <div><FormattedMessage description='App.Course.Butter' defaultMessage='máslo' /></div>
                <div><FormattedMessage description='App.Course.Feta' defaultMessage='feta' /></div>
                <div><FormattedMessage description='App.Course.GoatCheese' defaultMessage='kozí sýr' /></div>
                <div><FormattedMessage description='App.Course.PartyCheese' defaultMessage='párty sýr' /></div>
                <div><FormattedMessage description='App.Course.Ricotta' defaultMessage='ricotta' /></div>
                <div><FormattedMessage description='App.Course.Mozarella' defaultMessage='mozarella' /></div>
            </Col>
            <div className='free-image-block'  style={ matches ? { right: '25%' } : { bottom: '10%', right: '5%' }}><Radish size={ 100 } /></div>
            <Col className='col-12 col-lg-4'>
                <div><FormattedMessage description='App.Course.Mayones' defaultMessage='majonéza' /></div>
                <div><FormattedMessage description='App.Course.WhiteYogurt' defaultMessage='bílý jogurt' /></div>
                <div><FormattedMessage description='App.Course.ChocoYogurt' defaultMessage='čokoládový jogurt' /></div>
                <div><FormattedMessage description='App.Course.SoftRipenedCheese' defaultMessage='jemný zrající sýr' /></div>
                <div><FormattedMessage description='App.Course.RipenedCheese' defaultMessage='zrající čedar' /></div>
            </Col>
        </Row>
        <Row className='text-block'>
            <Col>
                <div><LinkButton variant='light-brown' to={ courseUrl }><FormattedMessage description='App.Course.IWantCourseBtn' defaultMessage='Chci kurz' /></LinkButton></div>
            </Col>
        </Row>
        <Row className='text-block border border-5 border-light-brown'>
            <Col>
                <h2><FormattedMessage description='App.Course.MoreOver' defaultMessage='Dále se dozvíte:' /></h2>
                <div><FormattedMessage description='App.Course.More.1' defaultMessage='Proč je rostlinná strava skvělá' /></div>
                <div><FormattedMessage description='App.Course.More.2' defaultMessage='Proč vyrábět rostlinné sýry doma' /></div>
                <div><FormattedMessage description='App.Course.More.3' defaultMessage='Co od kurzu můžete očekávat' /></div>
                <div><FormattedMessage description='App.Course.More.4' defaultMessage='Co očekávat od rostlinných alternativ mléčných výrobků' /></div>
                <div><FormattedMessage description='App.Course.More.5' defaultMessage='Kde nakoupit suroviny' /></div>
                <div><FormattedMessage description='App.Course.More.6' defaultMessage='Jaké vybavení potřebujete' /></div>
            </Col>
        </Row>
        <Row className='text-block'>
            <Col>
                <div><LinkButton variant='light-brown' to={ courseUrl }><FormattedMessage description='App.Course.IWantCourseBtn' defaultMessage='Chci kurz' /></LinkButton></div>
                <div className='image-block'><Radish size={ 100 } rotation={ 270 } /></div>      
            </Col>
        </Row>
        <Row className='text-block bg-light-brown'>
            <Col>
                <h3><FormattedMessage description='App.Course.BonusHeader' defaultMessage='a jako bonus vás čeká recept na ořechovou paštiku' /></h3>
            </Col>
        </Row>
        <Ratio aspectRatio={ 4000 / 6000 } className='image-block'>
            <img src={ walnutPate.src } alt={ intl.formatMessage({ description: 'App.Course.WalnutPate', defaultMessage: 'Walnut Paté' }) } />
        </Ratio>
        <Row className='text-block'>
            <Col>
                <div><FormattedMessage description='App.Course.BonusText.1' defaultMessage='Domácí výroba jídla je velká radost. Skrz své ruce a smysly se propojíme se surovinami a do procesu vaření vnášíme spoustu své pozornosti a lásky. Cokoli, co si sami vyrobíme, nám přináší mnohem větší uspokojení z jídla než zakoupené produkty z obchodu.' /></div>
                <div className='pt-3'><FormattedMessage description='App.Course.BonusText.2' defaultMessage='Díky tomuto kurzu budete moci svým nejdražším dopřát domácí máslo, sýry a jogurty. A budete mít jistotu, že jste do všech svých výrobků dali nejen ty nejkvalitnější rostlinné ingredience, ale navrch i svou dobrou energii. Jídlo pak přestává být pouhou potravou a stává se léčbou a zdrojem životní pohody.' /></div>
            </Col>
        </Row>
        <Ratio aspectRatio={ 861 / 1414 } className='image-block'>
            <img src={ hands.src } alt={ intl.formatMessage({ description: 'App.Course.Hands', defaultMessage: 'Hands' }) } />
        </Ratio>
        <Row  className='text-block'>
            <Col>
                <div><LinkButton variant='light-brown' to={ courseUrl }><FormattedMessage description='App.Course.IWantToProduce' defaultMessage='CHCI VYRÁBĚT SVOJE VEGANSKÉ SÝRY' /></LinkButton></div>
            </Col>
        </Row>
        <Row className='text-block'>
            <Col>
                <div className='image-block'><Radish size={ 100 } rotation={ 90 } /></div>
                <div><FormattedMessage description='App.Course.IWantToProduce.1' defaultMessage='Vyrobit si u sebe doma v kuchyni rostlinný sýr z ořechů není nic těžkého. S tímto kurzem to úplně snadno zvládnete a žádný jiný zdroj informací nebudete potřebovat.' /></div>
                <div className='pt-3'><FormattedMessage description='App.Course.IWantToProduce.2' defaultMessage='Přečetla jsem za vás spoustu kuchařek a blogů, viděla nespočet videonávodů a vše poctivě odzkoušela. Ochutnávali nejen moji chlapi doma, ale i spousta přátel a úplně cizích lidí, kteří běžně jedí mléčné výrobky. Odložila jsem spoustu nevyhovujících návodů, zkazila a vyhodila hodně nepovedených výrobků a provařila mnoho peněz. Proto vy už nemusíte! To nejlepší vás naučím v tomto kurzu.' /></div>
                <div className='pt-3'><FormattedMessage description='App.Course.IWantToProduce.3' defaultMessage='Získáte přesné a snadné návody na 17 základních veganských alternativ oblíbených mléčných výrobků, jako je máslo, jogurt, zakysaná smetana, zrající sýr, parmezán, feta, majonéza apod. A bonusový recept na paštiku, protože ten prostě musíte mít!' /></div>
            </Col>
        </Row>
        <Ratio aspectRatio={ 4000 / 6000 } className='image-block'>
            <img src={ ricota.src } alt={ intl.formatMessage({ description: 'App.Course.Ricota', defaultMessage: 'Ricota' }) } />
        </Ratio>
        <Row className='text-block'>
            <Col>
                <div><LinkButton variant='light-brown' to={ courseUrl }><FormattedMessage description='App.Course.IWillMakeIt' defaultMessage='Jdu na TO!' /></LinkButton></div>
            </Col>
        </Row>
        <Row>
            <Col>
                <div className='image-block'><img src={ logo.src } alt='Logo Vegilove.cz' width='300' /></div>
            </Col>
        </Row>
        <Ratio aspectRatio={ 2351 / 3535 } className='image-block'>
            <img src={ cow.src } alt={ intl.formatMessage({ description: 'App.Course.Cow', defaultMessage: 'Cow' }) } />
        </Ratio>
        <Row className='text-block'>
            <Col>
                <div className='sweet-belly fs-1'>{ intl.formatMessage({ description: 'App.Course.FinalWords', defaultMessage: '... z lásky k sobě, zvířatům i planetě!' }) }</div>
                <div className='image-block'><Radish size={ 100 } rotation={ 270 } /></div>
            </Col>
        </Row>
    </div>
}

export default Course